import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { DoorOpen, Search, Save, Check, Users } from "lucide-react";

/**
 * Bulk Room Assignment Dialog — select many students + one target room → single API call.
 * Props: open, onClose, children[], teachers[], onSaved()
 */
export default function BulkRoomAssignDialog({ open, onClose, children, teachers, onSaved }) {
  const [selected, setSelected] = useState(new Set());
  const [targetRoom, setTargetRoom] = useState("__none");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setQ("");
    setTargetRoom("__none");
  }, [open]);

  const teacherRoomFor = (child) => {
    const tid = (child.assigned_teacher_ids || [])[0];
    const t = teachers.find((x) => x.id === tid);
    return t?.room_number || null;
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return children;
    return children.filter((c) => (c.name || "").toLowerCase().includes(t));
  }, [q, children]);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async () => {
    if (selected.size === 0) { toast.error("اختر طالباً واحداً على الأقل"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/children/bulk-room", {
        child_ids: Array.from(selected),
        room_number: targetRoom === "__none" ? null : Number(targetRoom),
      });
      toast.success(`✔️ تم تحديث ${data.modified} طالب${targetRoom === "__none" ? " (إزالة القاعة)" : ` إلى القاعة ${data.room_number}`}`);
      onSaved?.();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-room-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black flex items-center gap-2">
            <DoorOpen className="w-6 h-6 text-indigo-600" /> إسناد جماعي لقاعة
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-4 mb-4">
          <div className="text-xs font-black text-indigo-900 mb-2">القاعة الهدف</div>
          <Select value={targetRoom} onValueChange={setTargetRoom}>
            <SelectTrigger data-testid="bulk-room-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— إزالة القاعة (تلقائي حسب المعلم) —</SelectItem>
              {[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>قاعة {n}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-indigo-700 mt-1.5">
            سيُطبَّق هذا الاختيار على كل الطلاب المحدَّدين أدناه بضغطة واحدة.
          </div>
        </div>

        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input dir="rtl" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث باسم الطالب..." className="pr-9" data-testid="bulk-room-search" />
        </div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-indigo-700 bg-indigo-50 rounded-full px-3 py-1 flex items-center gap-1">
            <Users className="w-3 h-3" /> محدَّد: {selected.size} من {children.length}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set(filtered.map((c) => c.id)))} data-testid="bulk-room-select-all">تحديد المرئي</Button>
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} data-testid="bulk-room-clear">مسح</Button>
          </div>
        </div>

        <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">لا توجد نتائج</div>
          ) : filtered.map((c) => {
            const on = selected.has(c.id);
            const currentRoom = c.room_number || teacherRoomFor(c);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${on ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white hover:border-indigo-200"}`}
                data-testid={`bulk-room-child-${c.id}`}
              >
                <input type="checkbox" checked={on} onChange={() => toggle(c.id)} className="w-4 h-4 accent-indigo-600" />
                <div className="flex-1 font-bold text-sm text-slate-800">{c.name}</div>
                {currentRoom ? (
                  <span className="text-[10px] font-black text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">قاعة {currentRoom}</span>
                ) : (
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">بدون</span>
                )}
                {on && <Check className="w-4 h-4 text-indigo-600" />}
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} disabled={saving}>إلغاء</Button>
          <Button onClick={save} disabled={saving} className="gap-2 bg-gradient-to-l from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700" data-testid="bulk-room-save">
            <Save className="w-4 h-4" /> {saving ? "جارٍ الحفظ..." : `تطبيق (${selected.size})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
